import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { InvoiceRequestForm } from "@/components/invoice-requests/invoice-request-form";
import { Content } from "@/components/layouts/content";
import {
	invoiceRequestsQueryOptions,
	useInvoiceRequestCorrectionsMutation,
	useInvoiceRequestCreateMutation,
	useInvoiceRequestDeleteMutation,
	useInvoiceRequestMarkSentMutation,
	useInvoiceRequestNotifyClientMutation,
	useInvoiceRequestPaymentStatusMutation,
	useInvoiceRequestStatusMutation,
	useInvoiceRequestUpdateMutation,
	useInvoiceRequestUploadAttachmentMutation,
} from "@/features/dashboard/api/queries/invoice-request-query";
import type { CreateInvoiceRequestData, InvoiceRequest } from "@/features/dashboard/types/invoice-request";
import { queryClient } from "@/query-client";
import { formatNumber } from "@/utils/format-number";
import * as Dialog from "@radix-ui/react-dialog";
import {
	IconArchive,
	IconCash,
	IconCheck,
	IconChevronDown,
	IconChevronUp,
	IconDots,
	IconEdit,
	IconExternalLink,
	IconFileDownload,
	IconFileTypePdf,
	IconFileWord,
	IconInfoCircle,
	IconPaperclip,
	IconPencilMinus,
	IconPlus,
	IconReceipt,
	IconSearch,
	IconSend,
	IconX,
	IconXboxX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import * as Popover from "@radix-ui/react-popover";


type DerivedStatus = "pending" | "approved" | "processed" | "outstanding" | "overdue" | "paid" | "rejected";
type DerivedBillingStatus = "pending" | "sent_billed" | "cancelled";

const statusLabels: Record<DerivedStatus, string> = {
	pending: "Pending",
	approved: "Approved",
	processed: "Processed",
	outstanding: "Outstanding",
	overdue: "Overdue",
	paid: "Paid",
	rejected: "Rejected",
};

const statusFilterLabels: Record<string, string> = {
	...statusLabels,
};



const statusColors: Record<DerivedStatus, string> = {
	pending: "bg-yellow-100 text-yellow-800",
	approved: "bg-blue-100 text-blue-800",
	processed: "bg-green-100 text-green-800",
	outstanding: "bg-orange-100 text-orange-800",
	overdue: "bg-orange-100 text-orange-800",
	paid: "bg-emerald-100 text-emerald-800",
	rejected: "bg-red-100 text-red-800",
};

const billingStatusLabels: Record<DerivedBillingStatus, string> = {
	pending: "Pending",
	sent_billed: "Sent / Billed",
	cancelled: "Cancelled",
};

const billingStatusFilterLabels: Record<string, string> = {
	...billingStatusLabels,
};

const billingStatusColors: Record<DerivedBillingStatus, string> = {
	pending: "bg-gray-100 text-gray-800",
	sent_billed: "bg-purple-100 text-purple-800",
	cancelled: "bg-red-100 text-red-800",
};

const deliveryMethodLabels: Record<string, string> = {
	email: "Email",
	courier: "Courier",
	in_person: "In-person",
	other: "Other",
};

function deriveStatus(data: InvoiceRequest): DerivedStatus {
	if (data.status === "rejected") return "rejected";
	if (data.status === "pending") return "pending";
	if (data.status === "approved") return "approved";

	if (data.status === "processed") {
		if (!data.sent_billed_at) return "processed";
		if (data.payment_status === "paid") return "paid";
		if (data.payment_status === "partial") return "outstanding";
		const isOverdue = !!data.payment_due_date && new Date(data.payment_due_date) < new Date();
		if (isOverdue) return "outstanding";
		return "processed";
	}

	return "pending";
}

function deriveBillingStatus(data: InvoiceRequest): DerivedBillingStatus {
	if (data.status === "rejected") return "cancelled";
	if (data.sent_billed_at) return "sent_billed";
	return "pending";
}

const billingTypeLabels: Record<string, string> = {
	one_time: "One Time",
	recurring: "Recurring",
	mixed: "Mixed",
};

function getDisplayAmount(data: InvoiceRequest): string {
	if (data.billing_type === "one_time" && data.amount_one_time) return `\u20B1${formatNumber(Number(data.amount_one_time))}`;
	if (data.billing_type === "recurring" && data.amount_recurring) return `\u20B1${formatNumber(Number(data.amount_recurring))}`;
	if (data.billing_type === "mixed") {
		const parts: string[] = [];
		if (data.amount_one_time) parts.push(`OT: \u20B1${formatNumber(Number(data.amount_one_time))}`);
		if (data.amount_recurring) parts.push(`RR: \u20B1${formatNumber(Number(data.amount_recurring))}`);
		if (data.amount_mixed) parts.push(`Mixed: \u20B1${formatNumber(Number(data.amount_mixed))}`);
		return parts.join(" / ") || "-";
	}
	return "-";
}

function useColumns({
	onEdit,
	onDelete,
	onApprove,
	onSentBilled,
	onReject,
	onGenerate,
	onMarkAsPaid,
	onDownloadReceipt,
	onUploadAttachment,
	onCorrections,
	onNotifyClient,
}: {
	onEdit: (data: InvoiceRequest) => void;
	onDelete: (data: InvoiceRequest) => void;
	onApprove: (data: InvoiceRequest) => void;
	onSentBilled: (data: InvoiceRequest) => void;
	onReject: (data: InvoiceRequest) => void;
	onGenerate: (data: InvoiceRequest) => void;
	onMarkAsPaid: (data: InvoiceRequest) => void;
	onDownloadReceipt: (data: InvoiceRequest) => void;
	onUploadAttachment: (data: InvoiceRequest) => void;
	onCorrections: (data: InvoiceRequest) => void;
	onNotifyClient: (data: InvoiceRequest) => void;
}): ColumnDef<InvoiceRequest>[] {
	return [
		// IR menu button
		{
			id: "actions",
			label: "",
			cellClassName: "w-[50px]",
			cell: ({ data }) => (
				<DropdownMenu modal={false}>
					<DropdownMenuTrigger asChild>
						<Button className="!rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
							<IconDots size={16} stroke={1.5} />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						{data.status === "pending" && (
							<DropdownMenuItem onSelect={() => onEdit(data)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconPencilMinus size={16} stroke={2} />
									<span>Edit</span>
								</div>
							</DropdownMenuItem>
						)}
						{data.sales_contract_id && (
							<DropdownMenuItem asChild>
								<a
									href={`/sales-contracts/${data.sales_contract_id}`}
									target="_blank"
									rel="noreferrer"
									className="flex flex-row items-center w-full py-1 px-2 gap-2"
								>
									<IconExternalLink size={16} stroke={2} />
									<span>View Contract</span>
								</a>
							</DropdownMenuItem>
						)}
						{data.project_id && (
							<DropdownMenuItem asChild>
								<a href={`/projects/${data.project_id}`} target="_blank" rel="noreferrer" className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconExternalLink size={16} stroke={2} />
									<span>View Project</span>
								</a>
							</DropdownMenuItem>
						)}
						{data.status === "approved" && (
							<DropdownMenuItem onSelect={() => onGenerate(data)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconFileDownload size={16} stroke={2} />
									<span>Generate Invoice</span>
								</div>
							</DropdownMenuItem>
						)}
						{(data.status === "approved" || data.status === "processed") && (
							<DropdownMenuItem onSelect={() => onCorrections(data)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconEdit size={16} stroke={2} />
									<span>Corrections</span>
								</div>
							</DropdownMenuItem>
						)}
						{data.contact_email && (
							<DropdownMenuItem onSelect={() => setTimeout(() => onNotifyClient(data), 0)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconSend size={16} stroke={2} />
									<span>Notify Client</span>
								</div>
							</DropdownMenuItem>
						)}
						{data.status === "pending" && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => onApprove(data)}>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
										<IconCheck size={16} stroke={2} />
										<span>Approve</span>
									</div>
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => onReject(data)}>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-2 text-red-600">
										<IconXboxX size={16} stroke={2} />
										<span>Reject</span>
									</div>
								</DropdownMenuItem>
							</>
						)}

						{data.status === "approved" && !data.sent_billed_at && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => onSentBilled(data)}>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-2 text-purple-800">
										<IconCash size={16} stroke={2} />
										<span>Sent / Billed</span>
									</div>
								</DropdownMenuItem>
							</>
						)}

						{data.status === "processed" && data.sent_billed_at && data.payment_status !== "paid" && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => onMarkAsPaid(data)}>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-2 text-green-600">
										<IconCash size={16} stroke={2} />
										<span>Mark as Paid</span>
									</div>
								</DropdownMenuItem>
							</>
						)}
						{(data.payment_status === "paid" || data.payment_status === "partial") && data.receipt_number && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => onDownloadReceipt(data)}>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
										<IconReceipt size={16} stroke={2} />
										<span>Download Receipt</span>
									</div>
								</DropdownMenuItem>
							</>
						)}
						{(data.payment_status === "paid" || data.payment_status === "partial") && (
							<DropdownMenuItem onSelect={() => setTimeout(() => onUploadAttachment(data), 0)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconPaperclip size={16} stroke={2} />
									<span>Add Attachment</span>
								</div>
							</DropdownMenuItem>
						)}
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => onDelete(data)} dangerZone>
							<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
								<IconArchive size={16} stroke={2} />
								<span>Archive</span>
							</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
		// 
		{
			id: "invoice_number",
			label: "Invoice No.",
			cellClassName: "min-w-[120px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => (
				<Link
					to="/invoice-requests/$invoiceRequestId"
					params={{ invoiceRequestId: data.id! }}
					className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
				>
					{data.invoice?.invoice_number || data.id?.slice(0, 8) || "-"}
				</Link>
			),
		},
		{
			id: "invoice_date",
			label: "Invoice Date",
			cellClassName: "min-w-[110px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => (
				<span className="text-gray-900 text-sm">{data.invoice_date_requested ? new Date(data.invoice_date_requested).toLocaleDateString() : "-"}</span>
			),
		},
		{
			id: "project_client",
			label: "Project / Client",
			cellClassName: "min-w-[200px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => (
				<div className="flex flex-col">
					<span className="font-medium text-gray-900 text-sm">{data.project_name || data.project?.name || "-"}</span>
					<span className="text-xs text-gray-500">{data.client_name || "-"}</span>
				</div>
			),
		},
		{
			id: "amount",
			label: "Amount",
			cellClassName: "min-w-[130px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="font-medium text-gray-900 text-sm">{getDisplayAmount(data)}</span>,
		},
		{
			id: "tin_no",
			label: "TIN",
			cellClassName: "min-w-[120px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm">{data.tin_no || "-"}</span>,
		},
		{
			id: "client_address",
			label: "Address",
			cellClassName: "min-w-[180px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm line-clamp-2">{data.client_address || "-"}</span>,
		},
		{
			id: "invoice",
			label: "Invoice",
			cellClassName: "min-w-[140px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => {
				if (data.status !== "approved" && data.status !== "processed") return <span className="text-gray-400 text-sm">-</span>;
				return (
					<div className="flex gap-1">
						<button
							type="button"
							onClick={() => window.open(`/api/invoice-requests/${data.id}/generate-document-pdf`, "_blank")}
							className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
							title="Download PDF"
						>
							<IconFileTypePdf size={14} stroke={2} />
							<span>PDF</span>
						</button>
					</div>
				);
			},
		},
		{
			id: "status",
			label: "Status",
			cellClassName: "min-w-[110px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => {
				const derived = deriveStatus(data);
				const isOutstanding = derived === "outstanding";
				const isPaid = derived === "paid";
				return (
					<div className="flex flex-col gap-0.5">
						<span className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${statusColors[derived]}`}>
							{statusLabels[derived]}
						</span>
						{data.payment_due_date && (isOutstanding || (derived === "processed" && data.sent_billed_at)) && !isPaid && (
							<span className={`text-[10px] ${isOutstanding ? "text-red-500 font-medium" : "text-gray-400"}`}>
								Due: {new Date(data.payment_due_date).toLocaleDateString()}
							</span>
						)}
					</div>
				);
			},
		},
		{
			id: "billing_status",
			label: "Billing Status",
			cellClassName: "min-w-[110px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => {
				const derived = deriveBillingStatus(data);
				const chip = (
					<div className={`flex flex-col gap-0.5 ${derived === "sent_billed" ? "cursor-pointer" : ""}`}>
						<span className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${billingStatusColors[derived]}`}>
							{billingStatusLabels[derived]}
						</span>
						{derived === "sent_billed" && data.sent_billed_at && (
							<span className="text-[10px] text-gray-400 cursor-pointer">
								{new Date(data.sent_billed_at).toLocaleDateString()}
								{data.delivery_method && ` · ${deliveryMethodLabels[data.delivery_method] || data.delivery_method}`}
							</span>
						)}
					</div>
				);
				if (derived !== "sent_billed") return chip;
				return (
					<Popover.Root>
						<Popover.Trigger asChild>{chip}</Popover.Trigger>
						<Popover.Portal>
							<Popover.Content className="z-50 w-72 rounded-lg border bg-white p-4 shadow-lg text-sm" sideOffset={4}>
								<div className="font-semibold mb-2">Billing Details</div>
								<dl className="space-y-1.5">
									<div className="flex justify-between gap-3">
										<dt className="text-gray-500">Date sent</dt>
										<dd>{data.sent_billed_at ? new Date(data.sent_billed_at).toLocaleString() : "—"}</dd>
									</div>
									<div className="flex justify-between gap-3">
										<dt className="text-gray-500">Method</dt>
										<dd>{data.delivery_method ? deliveryMethodLabels[data.delivery_method] || data.delivery_method : "—"}</dd>
									</div>
									<div className="flex justify-between gap-3">
										<dt className="text-gray-500">Sent by</dt>
										<dd>{data.sent_billed_by_user?.name || "—"}</dd>
									</div>
									{data.recipient_notes && (
										<div className="pt-2 border-t mt-2">
											<dt className="text-gray-500 mb-1">Notes</dt>
											<dd className="text-gray-800 whitespace-pre-wrap">{data.recipient_notes}</dd>
										</div>
									)}
								</dl>
							</Popover.Content>
						</Popover.Portal>
					</Popover.Root>
				);
			},
		},
	];
}

export const Route = createFileRoute("/_dashboard/invoice-requests")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		"filter[status]": z.string().optional(),
		"filter[billing_status]": z.string().optional(),
		"filter[billing_type]": z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	component: InvoiceRequestsPage,
});

function InvoiceRequestsPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingRequest, setEditingRequest] = useState<InvoiceRequest | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [generateRequest, setGenerateRequest] = useState<InvoiceRequest | null>(null);
	const [markAsProcessed, setMarkAsProcessed] = useState(true);
	const [paymentDueDate, setPaymentDueDate] = useState("");
	const [approveRequest, setApproveRequest] = useState<InvoiceRequest | null>(null);
	const [sentBilledRequest, setSentBilledRequest] = useState<InvoiceRequest | null>(null);
	const [sentDate, setSentDate] = useState("");
	const [deliveryMethod, setDeliveryMethod] = useState("");
	const [recipientNotes, setRecipientNotes] = useState("");
	const [markAsPaidRequest, setMarkAsPaidRequest] = useState<InvoiceRequest | null>(null);
	const [paymentAmount, setPaymentAmount] = useState("");
	const [paymentDateField, setPaymentDateField] = useState("");
	const [paymentMethod, setPaymentMethod] = useState("");
	const [paymentReferenceNumber, setPaymentReferenceNumber] = useState("");
	const [paymentAttachment, setPaymentAttachment] = useState<File | null>(null);
	const [uploadAttachmentRequest, setUploadAttachmentRequest] = useState<InvoiceRequest | null>(null);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [correctionsRequest, setCorrectionsRequest] = useState<InvoiceRequest | null>(null);
	const [notifyRequest, setNotifyRequest] = useState<InvoiceRequest | null>(null);

	// Mutatation
	const deleteRequestMutation = useInvoiceRequestDeleteMutation();
	const statusMutation = useInvoiceRequestStatusMutation();
	const markSentMutation = useInvoiceRequestMarkSentMutation();
	const paymentStatusMutation = useInvoiceRequestPaymentStatusMutation();
	const uploadAttachmentMutation = useInvoiceRequestUploadAttachmentMutation();

	const requestsQuery = useQuery(
		invoiceRequestsQueryOptions({
			...search,
		}),
	);

	// Search
	const handleSearch = async () => {
		await navigate({
			to: "/invoice-requests",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleClearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/invoice-requests",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const handleStatusFilter = async (status: string | undefined) => {
		await navigate({
			to: "/invoice-requests",
			search: (old) => ({
				...old,
				"filter[status]": status,
				page: 1,
			}),
		});
	};

	const handleBillingStatusFilter = async (billing_status: string | undefined) => {
		await navigate({
			to: "/invoice-requests",
			search: (old) => ({
				...old,
				"filter[billing_status]": billing_status,
				page: 1,
			}),
		});
	};


	// Table Menu handler (archive)
	const handleDeleteRequest = async (request: InvoiceRequest) => {
		if (request.id) {
			await deleteRequestMutation.mutateAsync(request.id);
			await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			toast.success("Invoice request deleted successfully");
		}
	};

	const handleApprove = (request: InvoiceRequest) => {
		setApproveRequest(request);
	};

	const confirmApprove = async () => {
		if (approveRequest?.id) {
			await statusMutation.mutateAsync({ id: approveRequest.id, status: "approved" });
			await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			toast.success("Invoice request approved");
		}
		setApproveRequest(null);
	};

	const handleSentBilled = (request: InvoiceRequest) => {
		setSentBilledRequest(request);
		setSentDate(new Date().toISOString().substring(0, 10));
		setDeliveryMethod("");
		setRecipientNotes("");
	}

	const confirmSentBilled = async () => {
		if (!sentBilledRequest?.id) return;
		if (!deliveryMethod) {
			toast.error("Please select a delivery method");
			return;
		}
		await markSentMutation.mutateAsync({
			id: sentBilledRequest.id,
			sent_date: sentDate || undefined,
			delivery_method: deliveryMethod,
			recipient_notes: recipientNotes || undefined,
		});
		await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
		toast.success("Marked as sent / billed");
		setSentBilledRequest(null);
	}

	const handleReject = async (request: InvoiceRequest) => {
		if (request.id) {
			await statusMutation.mutateAsync({ id: request.id, status: "rejected" });
			await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			toast.success("Invoice request rejected");
		}
	};

	const handleMarkAsPaid = (request: InvoiceRequest) => {
		setMarkAsPaidRequest(request);
		const totalDue = Number(request.total_amount_due || 0);
		const previouslyCollected = Number(request.payment_amount || 0);
		const remaining = totalDue - previouslyCollected;
		setPaymentAmount(String(remaining > 0 ? remaining : ""));
		setPaymentDateField(new Date().toISOString().substring(0, 10));
		setPaymentMethod(request.payment_method || request.payment_terms || "");
		setPaymentReferenceNumber("");
		setPaymentAttachment(null);
	};

	const confirmMarkAsPaid = async () => {
		if (!markAsPaidRequest?.id) return;
		if (!paymentAmount || !paymentDateField || !paymentMethod || !paymentReferenceNumber) {
			toast.error("Please fill in all payment details");
			return;
		}
		const totalDue = Number(markAsPaidRequest.total_amount_due || 0);
		const previouslyCollected = Number(markAsPaidRequest.payment_amount || 0);
		const thisPayment = Number(paymentAmount);
		const newTotal = previouslyCollected + thisPayment;
		const status = newTotal >= totalDue ? "paid" : "partial";

		await paymentStatusMutation.mutateAsync({
			id: markAsPaidRequest.id,
			payment_status: status,
			payment_amount: thisPayment,
			payment_date: paymentDateField,
			payment_method: paymentMethod,
			payment_reference_number: paymentReferenceNumber,
			payment_attachment: paymentAttachment || undefined,
		});
		await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
		toast.success(status === "paid" ? "Marked as fully paid" : "Partial payment recorded");
		setMarkAsPaidRequest(null);
	};


	const handleDownloadReceipt = (request: InvoiceRequest) => {
		if (request.id) {
			window.open(`/api/invoice-requests/${request.id}/generate-receipt`, "_blank");
		}
	};

	const handleUploadAttachment = (request: InvoiceRequest) => {
		setUploadAttachmentRequest(request);
		setUploadFile(null);
	};

	const confirmUploadAttachment = async () => {
		if (!uploadAttachmentRequest?.id || !uploadFile) return;
		await uploadAttachmentMutation.mutateAsync({ id: uploadAttachmentRequest.id, file: uploadFile });
		await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
		toast.success("Attachment uploaded");
		setUploadAttachmentRequest(null);
		setUploadFile(null);
	};

	const handleGenerateDocument = (request: InvoiceRequest) => {
		setGenerateRequest(request);
		setMarkAsProcessed(true);
		// Default to 30 days from today
		const defaultDue = new Date();
		defaultDue.setDate(defaultDue.getDate() + 30);
		setPaymentDueDate(defaultDue.toISOString().substring(0, 10));
	};

	const confirmGenerateDocument = async () => {
		if (!generateRequest?.id) return;
		window.open(`/api/invoice-requests/${generateRequest.id}/generate-document`, "_blank");
		if (markAsProcessed) {
			await statusMutation.mutateAsync({
				id: generateRequest.id,
				status: "processed",
				payment_due_date: paymentDueDate || undefined,
			});
			await queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			toast.success("Invoice generated and marked as processed");
		} else {
			toast.success("Invoice generated");
		}
		setGenerateRequest(null);
	};

	const columns = useColumns({
		onEdit: (request) => {
			setEditingRequest(request);
			setIsEditModalOpen(true);
		},
		onDelete: handleDeleteRequest,
		onApprove: handleApprove,
		onSentBilled: handleSentBilled,
		onReject: handleReject,
		onGenerate: handleGenerateDocument,
		onMarkAsPaid: handleMarkAsPaid,
		onDownloadReceipt: handleDownloadReceipt,
		onUploadAttachment: handleUploadAttachment,
		onCorrections: (request) => setCorrectionsRequest(request),
		onNotifyClient: (request) => setNotifyRequest(request),
	});

	const requests = requestsQuery.data?.data || [];
	const meta = requestsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 15,
		status_count: {},
	};

	if (requestsQuery.isLoading) {
		return (
			<Content pageName="Invoice Requests">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading invoice requests...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Invoice Requests">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Invoice Requests</span>
						<span className="text-base text-foreground/70">Manage billing and invoice requests</span>
					</div>
					<div className="flex gap-2">
						<Button onClick={() => setIsCreateModalOpen(true)}>
							<IconPlus size={18} stroke={2} />
							<span>New Request</span>
						</Button>
					</div>
				</div>

				<HowToCreateJournalEntries />

				{/* Search & Status Filter */}
				<div className="flex items-center gap-2 w-full">
					<div className="relative flex-1 max-w-md">
						<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
						<input
							type="text"
							placeholder="Search invoice requests..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={handleKeyPress}
							className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
						/>
						{searchTerm && (
							<button
								type="button"
								onClick={handleClearSearch}
								className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
							>
								<IconX size={16} />
							</button>
						)}
					</div>
					<Button onClick={handleSearch} variant="outline">
						Search
					</Button>
					<div className="flex flex-col gap-4">
						<div className="flex gap-1 ml-4">
							{[undefined, "pending", "approved", "processed", "outstanding", "overdue", "paid", "rejected"].map((status) => (
								<button
									key={status || "all"}
									type="button"
									onClick={() => handleStatusFilter(status)}
									className={clsx(
										"px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
										search["filter[status]"] === status || (!search["filter[status]"] && !status)
											? "bg-primary-600 text-white"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200",
									)}
								>
									{status ? statusFilterLabels[status] || status : "All Status"}
								</button>
							))}
						</div>
						<div className="flex gap-1 ml-4">
							{[undefined, "pending", "sent_billed", "cancelled"].map((billing_status) => (
								<button
									key={billing_status || "all"}
									type="button"
									onClick={() => handleBillingStatusFilter(billing_status)}
									className={clsx(
										"px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
										search["filter[billing_status]"] === billing_status || (!search["filter[billing_status]"] && !billing_status)
											? "bg-primary-600 text-white"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200",
									)}
								>
									{billing_status ? billingStatusFilterLabels[billing_status] || billing_status : "All Billing Status"}
								</button>
							))}
						</div>
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<DataTable
						className="border-collapse"
						data={requests}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/invoice-requests",
								search: (old) => ({
									...old,
									page,
								}),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
					/>
				</div>
			</div>

			{/* Create Modal */}
			<CreateInvoiceRequestModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={() => requestsQuery.refetch()} />

			{/* Approve Confirmation */}
			<Dialog.Root open={!!approveRequest} onOpenChange={() => setApproveRequest(null)}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
					<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
						<Dialog.Title className="text-lg font-semibold">Approve Invoice Request</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 mt-1">
							Are you sure you want to approve this invoice request
							{approveRequest?.client_name ? ` for ${approveRequest.client_name}` : ""}?
						</Dialog.Description>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="ghost" onClick={() => setApproveRequest(null)}>
								Cancel
							</Button>
							<Button onClick={confirmApprove}>Approve</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
			{/* Sent / Billed Confirmation */}
			<Dialog.Root open={!!sentBilledRequest} onOpenChange={() => setSentBilledRequest(null)}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
					<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
						<Dialog.Title className="text-lg font-semibold">Mark as Sent / Billed</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 mt-1">
							Record delivery details
							{sentBilledRequest?.client_name ? ` for ${sentBilledRequest.client_name}` : ""}.
						</Dialog.Description>
						<div className="mt-4 space-y-3">
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Sent Date</span>
								<input
									type="date"
									value={sentDate}
									onChange={(e) => setSentDate(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">
									Delivery Method <span className="text-red-500">*</span>
								</span>
								<select
									value={deliveryMethod}
									onChange={(e) => setDeliveryMethod(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								>
									<option value="">Select method...</option>
									<option value="email">Email</option>
									<option value="courier">Courier</option>
									<option value="in_person">In-person</option>
									<option value="other">Other</option>
								</select>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Notes / Recipient (optional)</span>
								<textarea
									value={recipientNotes}
									onChange={(e) => setRecipientNotes(e.target.value)}
									rows={3}
									placeholder="e.g. sent to accounting@client.com"
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								/>
							</label>
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="ghost" onClick={() => setSentBilledRequest(null)}>
								Cancel
							</Button>
							<Button onClick={confirmSentBilled} disabled={markSentMutation.isPending}>
								{markSentMutation.isPending ? "Saving..." : "Mark as Sent / Billed"}
							</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
			{/* Generate Invoice Confirmation */}
			<Dialog.Root open={!!generateRequest} onOpenChange={() => setGenerateRequest(null)}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
					<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
						<Dialog.Title className="text-lg font-semibold">Generate Invoice</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 mt-1">The invoice document will be downloaded.</Dialog.Description>
						<div className="mt-4 space-y-3">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={markAsProcessed}
									onChange={(e) => setMarkAsProcessed(e.target.checked)}
									className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
								/>
								<span className="text-sm text-gray-700">Mark as processed</span>
							</label>
							{markAsProcessed && (
								<label className="flex flex-col gap-1">
									<span className="text-sm font-medium text-gray-700">Payment Due Date</span>
									<input
										type="date"
										value={paymentDueDate}
										onChange={(e) => setPaymentDueDate(e.target.value)}
										className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</label>
							)}
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="ghost" onClick={() => setGenerateRequest(null)}>
								Cancel
							</Button>
							<Button onClick={confirmGenerateDocument}>Generate</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Mark as Paid Dialog */}
			<Dialog.Root open={!!markAsPaidRequest} onOpenChange={() => setMarkAsPaidRequest(null)}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
					<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
						<Dialog.Title className="text-lg font-semibold">Record Payment</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 mt-1">
							Enter payment details{markAsPaidRequest?.client_name ? ` for ${markAsPaidRequest.client_name}` : ""}.
						</Dialog.Description>
						{markAsPaidRequest &&
							(() => {
								const totalDue = Number(markAsPaidRequest.total_amount_due || 0);
								const prevCollected = Number(markAsPaidRequest.payment_amount || 0);
								const remaining = totalDue - prevCollected;
								return (
									<div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1 text-sm">
										<div className="flex justify-between">
											<span className="text-gray-600">Total Amount Due</span>
											<span className="font-medium">₱{formatNumber(totalDue)}</span>
										</div>
										{prevCollected > 0 && (
											<div className="flex justify-between">
												<span className="text-gray-600">Previously Collected</span>
												<span className="font-medium text-green-600">₱{formatNumber(prevCollected)}</span>
											</div>
										)}
										<div className="flex justify-between border-t border-gray-200 pt-1">
											<span className="font-medium text-gray-900">Remaining Balance</span>
											<span className="font-semibold text-gray-900">₱{formatNumber(remaining)}</span>
										</div>
									</div>
								);
							})()}
						<div className="mt-4 space-y-3">
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Payment Amount</span>
								<input
									type="number"
									step="0.01"
									max={Number(markAsPaidRequest?.total_amount_due || 0) - Number(markAsPaidRequest?.payment_amount || 0)}
									value={paymentAmount}
									onChange={(e) => setPaymentAmount(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									placeholder="0.00"
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Payment Date</span>
								<input
									type="date"
									value={paymentDateField}
									onChange={(e) => setPaymentDateField(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Payment Method</span>
								<select
									value={paymentMethod}
									onChange={(e) => setPaymentMethod(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								>
									<option value="">Select method...</option>
									<option value="COD">COD</option>
									<option value="Check / Direct Deposit">Check / Direct Deposit</option>
									<option value="Online Transfer">Online Transfer</option>
									<option value="Others">Others</option>
								</select>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Reference Number</span>
								<input
									type="text"
									value={paymentReferenceNumber}
									onChange={(e) => setPaymentReferenceNumber(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									placeholder="Enter reference number"
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">Attachment (optional)</span>
								<input
									type="file"
									accept="image/*,.pdf,.doc,.docx"
									onChange={(e) => setPaymentAttachment(e.target.files?.[0] || null)}
									className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
								/>
								{paymentAttachment && <span className="text-xs text-gray-500">{paymentAttachment.name}</span>}
							</label>
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="ghost" onClick={() => setMarkAsPaidRequest(null)}>
								Cancel
							</Button>
							<Button onClick={confirmMarkAsPaid} disabled={paymentStatusMutation.isPending}>
								{paymentStatusMutation.isPending
									? "Processing..."
									: (() => {
										const totalDue = Number(markAsPaidRequest?.total_amount_due || 0);
										const prevCollected = Number(markAsPaidRequest?.payment_amount || 0);
										const thisPayment = Number(paymentAmount || 0);
										return prevCollected + thisPayment >= totalDue ? "Confirm Full Payment" : "Record Partial Payment";
									})()}
							</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Upload Attachment Dialog */}
			<Dialog.Root open={!!uploadAttachmentRequest} onOpenChange={() => setUploadAttachmentRequest(null)}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
					<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
						<Dialog.Title className="text-lg font-semibold">Add Attachment</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 mt-1">
							Upload proof of payment or related documents
							{uploadAttachmentRequest?.client_name ? ` for ${uploadAttachmentRequest.client_name}` : ""}.
						</Dialog.Description>
						{uploadAttachmentRequest?.payment_attachments && uploadAttachmentRequest.payment_attachments.length > 0 && (
							<div className="mt-3 space-y-1">
								<span className="text-xs font-medium text-gray-500">Existing attachments:</span>
								{uploadAttachmentRequest.payment_attachments.map((att) => (
									<a key={att.id} href={att.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
										<IconPaperclip size={14} stroke={2} />
										{att.name}
									</a>
								))}
							</div>
						)}
						<div className="mt-4">
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">File</span>
								<input
									type="file"
									accept="image/*,.pdf,.doc,.docx"
									onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
									className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
								/>
							</label>
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="ghost" onClick={() => setUploadAttachmentRequest(null)}>
								Cancel
							</Button>
							<Button onClick={confirmUploadAttachment} disabled={!uploadFile || uploadAttachmentMutation.isPending}>
								{uploadAttachmentMutation.isPending ? "Uploading..." : "Upload"}
							</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Corrections Modal */}
			{correctionsRequest && (
				<CorrectionsModal invoiceRequest={correctionsRequest} onClose={() => setCorrectionsRequest(null)} onSuccess={() => requestsQuery.refetch()} />
			)}

			{/* Notify Client Modal */}
			{notifyRequest && <NotifyClientModal invoiceRequest={notifyRequest} onClose={() => setNotifyRequest(null)} />}

			{/* Edit Modal */}
			{editingRequest && (
				<EditInvoiceRequestModal
					invoiceRequest={editingRequest}
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setEditingRequest(null);
					}}
					onSuccess={() => requestsQuery.refetch()}
				/>
			)}
		</Content>
	);
}

// Create Modal
function CreateInvoiceRequestModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const createMutation = useInvoiceRequestCreateMutation();

	const handleSubmit = async (values: CreateInvoiceRequestData) => {
		setIsSubmitting(true);
		createMutation.mutate(values, {
			onSuccess: () => {
				toast.success("Invoice request created successfully");
				onSuccess();
				onClose();
				setIsSubmitting(false);
			},
			onError: () => {
				setIsSubmitting(false);
			},
		});
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">New Invoice Request</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<InvoiceRequestForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="invoice-request-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#invoice-request-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Creating..." : "Create Request"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

// Edit Modal
function EditInvoiceRequestModal({
	invoiceRequest,
	isOpen,
	onClose,
	onSuccess,
}: { invoiceRequest: InvoiceRequest; isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useInvoiceRequestUpdateMutation(invoiceRequest.id || "");

	const handleSubmit = async (values: CreateInvoiceRequestData) => {
		setIsSubmitting(true);
		updateMutation.mutate(
			{ ...values, id: invoiceRequest.id || "" },
			{
				onSuccess: () => {
					toast.success("Invoice request updated successfully");
					onSuccess();
					onClose();
					setIsSubmitting(false);
				},
				onError: () => {
					setIsSubmitting(false);
				},
			},
		);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Invoice Request</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<InvoiceRequestForm invoiceRequest={invoiceRequest} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="invoice-request-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#invoice-request-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Updating..." : "Update Request"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

interface CorrectionItem {
	quantity: number;
	unit_of_measurement: string;
	item_description: string;
	unit_price: number;
	total_price: number;
}

function CorrectionsModal({ invoiceRequest, onClose, onSuccess }: { invoiceRequest: InvoiceRequest; onClose: () => void; onSuccess: () => void }) {
	const correctionsMutation = useInvoiceRequestCorrectionsMutation();

	const getDefaultItems = (): CorrectionItem[] => {
		if (invoiceRequest.items && invoiceRequest.items.length > 0) {
			return invoiceRequest.items.map((item) => ({
				quantity: Number(item.quantity),
				unit_of_measurement: item.unit_of_measurement,
				item_description: item.item_description,
				unit_price: Number(item.unit_price),
				total_price: Number(item.total_price),
			}));
		}
		const amount = getDisplayAmountRaw(invoiceRequest);
		const description = invoiceRequest.project_name || invoiceRequest.sales_contract?.project_name || invoiceRequest.project?.name || "";
		return [
			{
				quantity: 1,
				unit_of_measurement: "LOT",
				item_description: description,
				unit_price: amount,
				total_price: amount,
			},
		];
	};

	const [items, setItems] = useState<CorrectionItem[]>(getDefaultItems);

	const updateItem = (index: number, field: keyof CorrectionItem, value: string | number) => {
		setItems((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [field]: value };
			if (field === "quantity" || field === "unit_price") {
				const qty = field === "quantity" ? Number(value) : updated[index].quantity;
				const price = field === "unit_price" ? Number(value) : updated[index].unit_price;
				updated[index].total_price = Math.round(qty * price * 100) / 100;
			}
			return updated;
		});
	};

	const addItem = () => {
		setItems((prev) => [...prev, { quantity: 1, unit_of_measurement: "LOT", item_description: "", unit_price: 0, total_price: 0 }]);
	};

	const removeItem = (index: number) => {
		if (items.length <= 1) return;
		setItems((prev) => prev.filter((_, i) => i !== index));
	};

	const handleSave = async () => {
		const hasEmpty = items.some((item) => !item.item_description.trim());
		if (hasEmpty) {
			toast.error("All items must have a description");
			return;
		}
		if (!invoiceRequest.id) return;
		await correctionsMutation.mutateAsync({ id: invoiceRequest.id, items });
		toast.success("Corrections saved successfully");
		onSuccess();
		onClose();
	};

	const grandTotal = items.reduce((sum, item) => sum + item.total_price, 0);

	return (
		<Dialog.Root open onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between mb-4">
						<div>
							<Dialog.Title className="text-xl font-semibold">Corrections</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 mt-1">
								Edit line items for {invoiceRequest.client_name || "this invoice request"}
							</Dialog.Description>
						</div>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>

					<div className="space-y-3">
						<div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
							<div className="col-span-1">Qty</div>
							<div className="col-span-2">Unit</div>
							<div className="col-span-4">Description</div>
							<div className="col-span-2">Unit Price</div>
							<div className="col-span-2">Total</div>
							<div className="col-span-1" />
						</div>

						{items.map((item, index) => (
							<div key={`correction-item-${index}`} className="grid grid-cols-12 gap-2 items-start">
								<div className="col-span-1">
									<input
										type="number"
										step="0.01"
										min="0"
										value={item.quantity}
										onChange={(e) => updateItem(index, "quantity", Number(e.target.value))}
										className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								<div className="col-span-2">
									<input
										type="text"
										value={item.unit_of_measurement}
										onChange={(e) => updateItem(index, "unit_of_measurement", e.target.value)}
										className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								<div className="col-span-4">
									<input
										type="text"
										value={item.item_description}
										onChange={(e) => updateItem(index, "item_description", e.target.value)}
										placeholder="Item description"
										className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								<div className="col-span-2">
									<input
										type="number"
										step="0.01"
										min="0"
										value={item.unit_price}
										onChange={(e) => updateItem(index, "unit_price", Number(e.target.value))}
										className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								<div className="col-span-2">
									<input
										type="number"
										step="0.01"
										value={item.total_price}
										onChange={(e) => updateItem(index, "total_price", Number(e.target.value))}
										className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								<div className="col-span-1 flex justify-center">
									<button
										type="button"
										onClick={() => removeItem(index)}
										disabled={items.length <= 1}
										className="p-1.5 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
										title="Remove item"
									>
										<IconX size={16} />
									</button>
								</div>
							</div>
						))}

						<button
							type="button"
							onClick={addItem}
							className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
						>
							<IconPlus size={14} stroke={2} />
							<span>Add Item</span>
						</button>

						<div className="flex justify-end border-t pt-3 mt-3">
							<div className="text-sm font-medium text-gray-700">
								Grand Total:{" "}
								<span className="text-lg font-semibold text-gray-900">
									{"\u20B1"}
									{formatNumber(grandTotal)}
								</span>
							</div>
						</div>
					</div>

					<div className="flex justify-end gap-2 mt-6">
						<Button variant="ghost" onClick={onClose}>
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={correctionsMutation.isPending}>
							{correctionsMutation.isPending ? "Saving..." : "Save Corrections"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function getDisplayAmountRaw(data: InvoiceRequest): number {
	if (data.total_amount_due) return Number(data.total_amount_due);
	if (data.billing_type === "one_time" && data.amount_one_time) return Number(data.amount_one_time);
	if (data.billing_type === "recurring" && data.amount_recurring) return Number(data.amount_recurring);
	if (data.billing_type === "mixed" && data.amount_mixed) return Number(data.amount_mixed);
	return 0;
}

function NotifyClientModal({ invoiceRequest, onClose }: { invoiceRequest: InvoiceRequest; onClose: () => void }) {
	const notifyMutation = useInvoiceRequestNotifyClientMutation();
	const projectName = invoiceRequest.project_name || invoiceRequest.sales_contract?.project_name || invoiceRequest.project?.name || "";
	const clientName = invoiceRequest.client_name || "";
	const totalAmount = getDisplayAmountRaw(invoiceRequest);

	const [email, setEmail] = useState(invoiceRequest.contact_email || "");
	const [subject, setSubject] = useState(
		invoiceRequest.invoice?.invoice_number
			? `Invoice ${invoiceRequest.invoice.invoice_number} - ${projectName || clientName}`
			: `Invoice Notification - ${projectName || clientName}`,
	);
	const [message, setMessage] = useState(
		`Please be informed that we have issued an invoice for ${projectName || "your project"}${totalAmount ? ` amounting to ₱${formatNumber(totalAmount)}` : ""}.\n\nKindly review the details and process the payment at your earliest convenience.\n\nShould you have any questions or require further clarification, please do not hesitate to reach out.`,
	);

	const handleSend = async () => {
		if (!email || !subject || !message || !invoiceRequest.id) return;
		try {
			await notifyMutation.mutateAsync({ id: invoiceRequest.id, email, subject, message });
			toast.success(`Notification sent to ${email}`);
			onClose();
		} catch {
			toast.error("Failed to send notification");
		}
	};

	return (
		<Dialog.Root open onOpenChange={() => onClose()}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
					<Dialog.Title className="text-lg font-semibold">Notify Client</Dialog.Title>
					<Dialog.Description className="text-sm text-gray-500 mt-1">Send an email notification to the client with invoice details.</Dialog.Description>

					<div className="mt-4 space-y-4">
						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">To</span>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
								placeholder="client@email.com"
							/>
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">Subject</span>
							<input
								type="text"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
							/>
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">Message</span>
							<textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								rows={6}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary resize-none"
							/>
							<span className="text-xs text-gray-400">Invoice details and line items will be included automatically.</span>
						</label>
					</div>

					<div className="flex justify-end gap-2 mt-6">
						<Button variant="ghost" onClick={onClose}>
							Cancel
						</Button>
						<Button onClick={handleSend} disabled={!email || !subject || !message || notifyMutation.isPending}>
							<IconSend size={16} stroke={2} />
							<span>{notifyMutation.isPending ? "Sending..." : "Send"}</span>
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function HowToCreateJournalEntries() {
	const [open, setOpen] = useState(() => {
		try {
			return localStorage.getItem("howto-je-dismissed") !== "true";
		} catch {
			return true;
		}
	});

	const handleToggle = () => {
		const next = !open;
		setOpen(next);
		try {
			localStorage.setItem("howto-je-dismissed", next ? "false" : "true");
		} catch {
			// ignore storage errors
		}
	};

	return (
		<div className="rounded-lg border border-blue-200 bg-blue-50">
			<button type="button" onClick={handleToggle} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left" aria-expanded={open}>
				<span className="flex items-center gap-2 text-sm font-semibold text-blue-900">
					<IconInfoCircle size={18} stroke={2} />
					How to send Journal Entries and the Collection Receipt to Books
				</span>
				{open ? <IconChevronUp size={16} stroke={2} className="text-blue-700" /> : <IconChevronDown size={16} stroke={2} className="text-blue-700" />}
			</button>

			{open && (
				<div className="border-t border-blue-200 px-4 py-4 text-sm text-blue-900 space-y-4">
					<div>
						<p className="font-medium mb-1">Where to find the tabs</p>
						<p className="text-blue-800">
							Open any invoice request from the table below. Inside the request detail page you'll see three tabs:{" "}
							<span className="font-semibold">Invoice Lines</span>, <span className="font-semibold">Journal Items</span>, and{" "}
							<span className="font-semibold">Collection Receipt</span> (the third tab only appears once a payment is recorded).
						</p>
					</div>

					<div>
						<p className="font-medium mb-1">1 · Journal Items (the invoice's AR entry)</p>
						<ol className="list-decimal pl-5 space-y-1 text-blue-800">
							<li>
								Open the request → click the <span className="font-semibold">Journal Items</span> tab.
							</li>
							<li>
								Review the auto-computed lines: <span className="font-mono">DR 1100 Accounts Receivable</span> /{" "}
								<span className="font-mono">CR 4010 Sales Revenue</span> / <span className="font-mono">CR 2030 Output VAT</span>. If any line should hit a
								different Books account, click the account cell and pick another one from the dropdown.
							</li>
							<li>
								Make sure the entry shows <span className="font-semibold">Balanced</span> (green).
							</li>
							<li>
								Click <span className="font-semibold">Send to Books</span>. The status badge flips from amber "Draft" to blue "Draft in Books" and you'll see
								the JE number (e.g. <span className="font-mono">JE-20260521-00001</span>).
							</li>
							<li>
								The accountant then opens that entry in Books (<span className="font-mono">/gl/journal-entries</span>) and clicks{" "}
								<span className="font-semibold">Post Entry</span> to commit it to the GL.
							</li>
						</ol>
					</div>

					<div>
						<p className="font-medium mb-1">2 · Collection Receipt (the payment JE)</p>
						<ol className="list-decimal pl-5 space-y-1 text-blue-800">
							<li>
								Mark the request as <span className="font-semibold">Paid</span> or <span className="font-semibold">Partial</span> (top-right of the page) and
								fill in the Payment Breakdown — Withholding Tax, Liquidated Damages, Retention, Municipal Charge, Bank Charge.
							</li>
							<li>
								Click the <span className="font-semibold">Collection Receipt</span> tab — it appears once a payment is recorded.
							</li>
							<li>
								Review the lines: <span className="font-mono">DR Cash</span> + <span className="font-mono">DR EWT / deductions</span> /{" "}
								<span className="font-mono">CR AR</span> (must equal the invoice total). Override any account if needed.
							</li>
							<li>
								Click <span className="font-semibold">Send to Books</span>. The Collection JE lands in Books as a separate entry with{" "}
								<span className="font-mono">source = COLLECTION</span>.
							</li>
							<li>Accountant posts it from Books to clear the invoice's AR.</li>
						</ol>
					</div>

					<div className="rounded border border-blue-300 bg-white px-3 py-2 text-xs text-blue-800">
						<span className="font-semibold">Notes:</span> the two JEs are independent — pushing one does not push the other. Edits made after "Send to Books"
						can be re-synced by clicking <span className="font-semibold">Re-send to Books</span>. Once an entry is posted in Books, it becomes immutable and the
						Send button disappears. Default account mappings live under <span className="font-mono">/journal-accounts</span> (Admin sidebar).
					</div>
				</div>
			)}
		</div>
	);
}
